// ./src/components/features/Connectors/ConfigEditors/GcpFirestoreConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  Text,
  Anchor,
  Alert,
  Code,
  NumberInput,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { GcpFirestoreConnectorConfig } from "@/types";

export interface GcpFirestoreConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: GcpFirestoreConnectorConfig = {
  projectId: "",
  credentials: "",
  databaseId: "(default)",
  collection: "",
  documentIdPattern: "{{requestId}}",
  ttlField: "",
  ttlDays: 365,
};

/**
 * Parse and validate GCP Firestore connector configuration
 */
const parseGcpFirestoreConfig = (
  jsonString: string
): GcpFirestoreConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const GcpFirestoreConnectorConfigEditor: React.FC<
  GcpFirestoreConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<GcpFirestoreConnectorConfig>(() =>
    parseGcpFirestoreConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseGcpFirestoreConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: GcpFirestoreConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleProjectIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, projectId: e.target.value });
  };

  const handleCredentialsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    updateConfig({ ...config, credentials: e.target.value });
  };

  const handleDatabaseIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, databaseId: e.target.value });
  };

  const handleCollectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, collection: e.target.value });
  };

  const handleDocumentIdPatternChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, documentIdPattern: e.target.value });
  };

  const handleTtlFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, ttlField: e.target.value });
  };

  const handleTtlDaysChange = (value: string | number) => {
    const numValue = typeof value === "string" ? parseInt(value, 10) : value;
    updateConfig({ ...config, ttlDays: isNaN(numValue) ? 365 : numValue });
  };

  // Validate credentials JSON
  const credentialsError = (() => {
    if (!config.credentials) return undefined;
    try {
      JSON.parse(config.credentials);
      return undefined;
    } catch {
      return "Must be valid JSON";
    }
  })();

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Access Firestore in{" "}
          <Anchor
            href="https://console.cloud.google.com/firestore"
            target="_blank"
            rel="noopener noreferrer"
          >
            GCP Console
          </Anchor>
          {" → Firestore. Ensure the service account has "}
          <Code>roles/datastore.user</Code>
          {" permission."}
        </Text>
      </Alert>

      {/* Project ID Field */}
      <TextInput
        label="Project ID"
        placeholder="my-project-123456"
        description="GCP Project ID"
        value={config.projectId}
        onChange={handleProjectIdChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Credentials Field */}
      <Textarea
        label="Service Account Credentials (JSON)"
        placeholder='{"type": "service_account", "project_id": "...", ...}'
        description="Paste the entire contents of the service account JSON key file"
        value={config.credentials}
        onChange={handleCredentialsChange}
        disabled={disabled}
        required
        size="md"
        minRows={4}
        maxRows={8}
        autosize
        error={credentialsError}
        styles={{
          input: {
            fontFamily: "monospace",
            fontSize: "12px",
          },
        }}
      />

      {/* Database ID Field */}
      <TextInput
        label="Database ID"
        placeholder="(default)"
        description="Firestore database ID (use '(default)' for the default database)"
        value={config.databaseId || "(default)"}
        onChange={handleDatabaseIdChange}
        disabled={disabled}
        size="md"
      />

      {/* Collection Field */}
      <TextInput
        label="Collection"
        placeholder="form-submissions"
        description="Name of the Firestore collection to store documents"
        value={config.collection}
        onChange={handleCollectionChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Document ID Pattern Field */}
      <TextInput
        label="Document ID Pattern"
        placeholder="{{requestId}}"
        description="Pattern for document IDs (must be unique within collection)"
        value={config.documentIdPattern || "{{requestId}}"}
        onChange={handleDocumentIdPatternChange}
        disabled={disabled}
        size="md"
      />

      {/* TTL Field */}
      <TextInput
        label="TTL Field Name"
        placeholder="expireAt"
        description="Optional field name for document expiration (requires Firestore TTL policy)"
        value={config.ttlField || ""}
        onChange={handleTtlFieldChange}
        disabled={disabled}
        size="md"
      />

      {/* TTL Days */}
      {config.ttlField && (
        <NumberInput
          label="TTL Days"
          placeholder="365"
          description="Number of days until document expires"
          value={config.ttlDays || 365}
          onChange={handleTtlDaysChange}
          disabled={disabled}
          size="md"
          min={1}
          max={3650}
        />
      )}

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{requestId}}"}</Code> — Submission ID (e.g.,
          20250530143022-ABCD)
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{formName}}"}</Code> — Form name (sanitized)
          <br />
          <Code>{"{{timestamp}}"}</Code> — Unix timestamp
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
          <br />
          <Code>{"{{randUuid}}"}</Code> — Random UUID
        </Text>
      </Alert>

      {/* TTL Note */}
      {config.ttlField && (
        <Alert
          icon={<IconInfoCircle size={16} />}
          color="yellow"
          variant="light"
        >
          <Text size="sm">
            <strong>Note:</strong> To enable automatic document deletion, you
            must configure a TTL policy on the <Code>{config.ttlField}</Code>{" "}
            field in Firestore. Documents will be deleted approximately 24 hours
            after the TTL timestamp.
          </Text>
        </Alert>
      )}
    </Stack>
  );
};
